import { Container } from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationDialog from "../components/Common/ConfirmationDialog";
import ErrorMessage from "../components/Common/ErrorMessage";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import { EditListDialog, FrequentListHeader, FrequentListTabs, ListInfoCard, NewListDialog } from "../components/FrequentList";
import AddItemForm from "../components/FrequentList/AddItemForm";
import ItemsList from "../components/FrequentList/ItemsList";
import { useFrequentLists } from "../contexts/FrequentListContext";
import { ShoppingListContext } from "../contexts/ShoppingListContext";
import { useConfirmation } from "../hooks/useConfirmation";
import { useDialog } from "../hooks/useDialog";
import { useToast } from "../hooks/useToast";
import { pageContainer } from "../styles/shoppingListStyles";

const FrequentListPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { frequentLists, loading, error, addFrequentList, updateFrequentList, deleteFrequentList, addItemToList, updateItemInList, deleteItemFromList, prepareItemsForCart } = useFrequentLists();

  const { addItems } = useContext(ShoppingListContext);

  const [activeListId, setActiveListId] = useState("");

  // Dialog management
  const newListDialog = useDialog();
  const editListDialog = useDialog();
  const { confirm, dialogProps } = useConfirmation();

  // State for list menu
  const [listMenuAnchorEl, setListMenuAnchorEl] = useState(null);
  const listMenuOpen = Boolean(listMenuAnchorEl);

  const [newListData, setNewListData] = useState({ name: "", interval: 7 });
  const [editListData, setEditListData] = useState(null);

  const lists = useMemo(() => {
    const defaultLists = [
      {
        id: "default",
        name: t("frequentItems.weeklyItems"),
        interval: 7,
        lastUsed: null,
        nextSuggestion: null,
        shoppingListItems: [],
      },
    ];
    return frequentLists?.length > 0 ? frequentLists : defaultLists;
  }, [frequentLists, t]);

  // Set default active list if none selected
  useEffect(() => {
    if (lists.length > 0 && (!activeListId || !lists.some((list) => list.id === activeListId))) {
      setActiveListId(lists[0].id);
    }
  }, [activeListId, lists]);

  const activeList = useMemo(() => lists.find((list) => list.id === activeListId) || lists[0], [activeListId, lists]);

  const handleListMenuOpen = useCallback((event) => {
    setListMenuAnchorEl(event.currentTarget);
  }, []);

  const handleListMenuClose = useCallback(() => {
    setListMenuAnchorEl(null);
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveListId(newValue);
  }, []);

  const handleOperationError = useCallback(
    (operation, error) => {
      toast.error(t("frequentItems.operationFailed", { operation }), error);
    },
    [t, toast],
  );

  const handleNewListDialogOpen = useCallback(() => {
    setNewListData({ name: "", interval: 7 });
    newListDialog.open();
    handleListMenuClose();
  }, [newListDialog, handleListMenuClose]);

  const handleCreateNewList = useCallback(() => {
    if (!newListData.name.trim()) {
      toast.warning(t("frequentItems.enterListName"));
      return;
    }

    const newList = {
      id: Date.now().toString(),
      name: newListData.name,
      interval: newListData.interval,
      lastUsed: null,
      nextSuggestion: null,
      shoppingListItems: [],
    };

    addFrequentList(newList)
      .then((id) => {
        setActiveListId(id || newList.id);
        newListDialog.close();
        toast.success(t("frequentItems.listCreatedSuccess"));
      })
      .catch((err) => handleOperationError("create list", err));
  }, [addFrequentList, newListData, t, toast, newListDialog, handleOperationError]);

  const handleEditListDialogOpen = useCallback(() => {
    setEditListData({
      name: activeList.name,
      interval: activeList.interval,
    });
    editListDialog.open();
    handleListMenuClose();
  }, [activeList, editListDialog, handleListMenuClose]);

  const handleUpdateList = useCallback(() => {
    if (!editListData.name.trim()) {
      toast.warning(t("frequentItems.enterListName"));
      return;
    }

    const listIndex = lists.findIndex((list) => list.id === activeListId);
    if (listIndex === -1) return;

    const updatedList = {
      ...activeList,
      name: editListData.name,
      interval: editListData.interval,
    };

    updateFrequentList(listIndex, updatedList)
      .then(() => {
        editListDialog.close();
        toast.success(t("frequentItems.listUpdatedSuccess"));
      })
      .catch((err) => handleOperationError("update list", err));
  }, [activeList, activeListId, editListData, lists, t, toast, updateFrequentList, editListDialog, handleOperationError]);

  const handleDeleteList = useCallback(async () => {
    const confirmed = await confirm({
      title: t("frequentItems.deleteList"),
      message: t("frequentItems.confirmDeleteList"),
      confirmText: t("frequentItems.delete"),
      confirmButtonColor: "error",
    });

    if (confirmed) {
      const listIndex = lists.findIndex((list) => list.id === activeListId);
      if (listIndex === -1) return;

      // Close any open menus first to prevent focus issues
      handleListMenuClose();

      deleteFrequentList(listIndex)
        .then(() => {
          setActiveListId(lists.filter((list) => list.id !== activeListId)[0]?.id || "");
          toast.success(t("frequentItems.listDeletedSuccess"));
        })
        .catch((err) => handleOperationError("delete list", err));
    }
  }, [activeListId, confirm, deleteFrequentList, handleListMenuClose, lists, t, toast, handleOperationError]);

  const handleAddListToCart = useCallback(async () => {
    const confirmed = await confirm({
      title: t("frequentItems.addToCart"),
      message: t("frequentItems.confirmAddToCart"),
      confirmText: t("frequentItems.addToCart"),
      confirmButtonColor: "primary",
    });

    if (confirmed) {
      if (!activeList?.shoppingListItems?.length) {
        toast.warning(t("frequentItems.noItemsToAdd"));
        return;
      }

      try {
        // First, get the items from the frequent list context in correct format
        const shoppingItems = await prepareItemsForCart(activeListId);

        if (shoppingItems && shoppingItems.length > 0) {
          await addItems(shoppingItems);
          toast.success(t("frequentItems.itemsAddedSuccess"));
        } else {
          toast.warning(t("frequentItems.noItemsAdded"));
        }
      } catch (err) {
        handleOperationError("add items to cart", err);
      }
    }
  }, [activeListId, activeList, prepareItemsForCart, addItems, confirm, t, toast, handleOperationError]);

  const handleAddItem = useCallback(
    (itemData) => {
      if (!activeListId) {
        toast.error(t("frequentItems.noActiveList"));
        return;
      }

      const itemToAdd = {
        ...itemData,
        purchased: false,
      };

      addItemToList(activeListId, itemToAdd)
        .then(() => {
          toast.success(t("frequentItems.itemAddedSuccess"));
        })
        .catch((err) => handleOperationError("add item", err));
    },
    [activeListId, addItemToList, t, toast, handleOperationError],
  );

  // Loading state
  if (loading) {
    return (
      <Container sx={pageContainer} disableGutters>
        <LoadingSpinner />
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container sx={pageContainer} disableGutters>
        <ErrorMessage error={`${t("frequentItems.errorLoadingLists")} - ${error.toString()}`} />
      </Container>
    );
  }

  return (
    <Container sx={pageContainer} disableGutters>
      <FrequentListHeader
        onNewList={handleNewListDialogOpen}
        onOpenMenu={handleListMenuOpen}
        listMenuAnchorEl={listMenuAnchorEl}
        listMenuOpen={listMenuOpen}
        onCloseMenu={handleListMenuClose}
        onEditList={handleEditListDialogOpen}
        onAddToCart={handleAddListToCart}
        canDelete={lists.length > 1}
        onDeleteList={handleDeleteList}
      />

      <FrequentListTabs lists={lists} activeListId={activeListId} onTabChange={handleTabChange} />

      <ListInfoCard activeList={activeList} onAddToCart={handleAddListToCart} />

      <AddItemForm handleAdd={handleAddItem} />

      <ItemsList activeList={activeList} updateItemInList={updateItemInList} deleteItemFromList={deleteItemFromList} />

      <NewListDialog open={newListDialog.isOpen} onClose={newListDialog.close} newListData={newListData} setNewListData={setNewListData} onCreateList={handleCreateNewList} />

      <EditListDialog open={editListDialog.isOpen} onClose={editListDialog.close} editListData={editListData} setEditListData={setEditListData} onUpdateList={handleUpdateList} />

      <ConfirmationDialog {...dialogProps} />
    </Container>
  );
};

export default FrequentListPage;
